import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Message, Progress } from 'semantic-ui-react'
import { FormProvider, useForm } from 'react-hook-form';
import Layout from '~/components/Layout';
import { handleErrors } from '~/utils/helpers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import notify from '~/components/Notify';
import { RouteTypes } from '~/routes/constants';
import { Nullable } from '~/types';
import { getNumberOfFeeds, getStepHeadMessage, getTransformFeedGroups, prepareDataForPayload } from './utils';
import cloneDeep from 'lodash.clonedeep'
import Dialog from '~/components/Dialog';
import ClimaCheckService, { ClimaCheckFeedType, ClimaCheckOptionsType, CowResultsType, CattleResultsType } from '~/api/services/climaCheck';
import { allSteps, StepId } from './constants'

// STYLES
import { CSSProperties, ThemeContext } from 'styled-components';
import { CheckClimaEl } from '../style'
import { AnsweredContainer, LogoWrapper, SubmitHeader } from './style';

// STEPS
import StepsContainer from './StepsContainer';
import FourthStep from './Steps/FourthStep';
import ThirdStep from './Steps/ThirdStep';
import EnergyModal from './Steps/EnergyModal';
import Results from './Steps/Results';
import FirstStep from './Steps/FirstStep';
import SecondStep from './Steps/SecondStep';
import SixthStep from './Steps/SixthStep';
import FifthStep from './Steps/FifthStep';
import SeventhStep from './Steps/SeventhStep';
import EightStep from './Steps/EightStep';
import NineStep from './Steps/NineStep';
import TenStep from './Steps/TenStep';
import ElevenStep from './Steps/ElevenStep';
import TwelveStep from './Steps/TwelveStep';
import ThirteenStep from './Steps/ThirteenStep';
import FourteenStep from './Steps/FourteenStep';
import FifthteenStep from './Steps/FifthteenStep';
import ForageProduction, { NO_SILAGE_VALUE } from './Steps/ForageProdStep';
import ForageFeed from './Steps/ForageFeed';

const FIRST_STEP_INDEX = 0

export const FeedPageContext = createContext(null)
export const StepsContext = createContext(null)

export type StepType = {
  id: string
  title: string
  icon?: string
  image?: string
  style?: CSSProperties
  calfStep?: boolean
  forageStep?: boolean
  feedGroupTab?: boolean
}

//refactor
// Step content and indicator should be in the same component
// Leave only after presentation
const firstStepFields = ['totalMilkAmount', 'fat', 'protein', 'milkPrice', 'ureaContent']
const MIN_DLG_CALVING_AGE = 24
const MAX_DLG_CALVING_AGE = 30
const NO_SILAGE_KEY = 1

const AnswerClimateCheck: React.FC = () => {
  const { t } = useTranslation()
  const { id, farmerId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { logoTitle } = useContext(ThemeContext)

  const [isBusy, setIsBusy] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [feedPage, setFeedPage] = useState(0)
  const [options, setOptions] = useState<Nullable<ClimaCheckOptionsType>>(null)
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const [energyCalcData, setEnergyCalcData] = useState(null)
  const [results, setResults] = useState<{
    cowResults: CowResultsType
    heiferResults: Nullable<CattleResultsType>
  }>({
    cowResults: null,
    heiferResults: null
  })
  const [loadingEnergyData, setLoadingEnergyData] = useState(false)
  const [isEnergyModalOpen, setIsEnergyModalOpen] = useState(false)
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false)
  const [steps, setSteps] = useState<StepType[]>(allSteps)
  const [lastStep, setLastStep] = useState(0)

  const methods = useForm({
    'mode': 'onSubmit'
  })

  const { watch, formState: { dirtyFields }, setValue } = methods

  const watchBreedField = watch('breeds')
  const watchFeedGroups = watch('feedGroups')
  const watchPhaseFeeding = watch('phaseFeeding')
  const watchRearingPhaseType = watch('rearingPhasesType')
  const watchRaisesHeifers = watch('raiseHeifersOnFarm')
  const watchHeiferRaisingMethod = watch('heiferRaisingMethodId')
  const watchFirstCalvingAge = watch('firstCalvingAge')
  const watchYoungCattleWeightBeforeCalving = watch('youngCattleWeightBeforeCalving')
  const watchGrowSilage = watch('growSilage')

  useEffect(() => {
    fetchOptions()
  }, [t, location.pathname])

  useEffect(() => {
    if(activeTab > lastStep) {
      setLastStep(activeTab)
    }
  }, [activeTab, lastStep])

  useEffect(() => {
    const isFeedRationsTabsActive = getFeedRationsTabActive()
    if (!isFeedRationsTabsActive && energyCalcData) {
      setEnergyCalcData(null)
    }
  }, [activeTab, energyCalcData])

  useEffect(() => {
    const isFeedRationsTabsActive = getFeedRationsTabActive()

    if (isFeedRationsTabsActive) {
      setEnergyCalcData(null)
    }
  }, [watchFeedGroups])

  useEffect(() => {
    if(watchRaisesHeifers) {
      if(!options?.applyForageProductionInDataCollection) {
        setSteps(allSteps.filter(step => !step.forageStep))
      } else {
        setSteps(allSteps)
      }
    } else {
      setSteps(steps.filter(step => !step?.calfStep))
    }
  }, [watchRaisesHeifers])

  useEffect(() => {
    if(watchGrowSilage === NO_SILAGE_VALUE) {
      setSteps(steps.filter(step => step.id !== StepId.FORAGE_FEEDS))
    } else if (Boolean(options?.applyForageProductionInDataCollection) &&
  watchGrowSilage && watchGrowSilage !== NO_SILAGE_VALUE) {
      if(watchRaisesHeifers) {
        setSteps(allSteps)
      } else {
        setSteps(allSteps.filter(step => !step?.calfStep))
      }
    }
  }, [watchGrowSilage, options, watchRaisesHeifers])

  useEffect(() => {
    const fieldNames = ['heiferRaisingMethodId', 'firstCalvingAge', 'youngCattleWeightBeforeCalving']
    const isAllFieldsEntered = watchFirstCalvingAge && watchHeiferRaisingMethod && watchYoungCattleWeightBeforeCalving
    const isFieldValueChanged = Boolean(Object.keys(dirtyFields).find(dirtyFieldKey => fieldNames.find(fieldName => fieldName === dirtyFieldKey)))
    const isCalvingAgeInInterval = parseInt(watchFirstCalvingAge) >= MIN_DLG_CALVING_AGE && parseInt(watchFirstCalvingAge) <= MAX_DLG_CALVING_AGE

    const youngCattleWeightInRange = parseInt(watchYoungCattleWeightBeforeCalving) >= 300 && parseInt(watchYoungCattleWeightBeforeCalving) <= 800

    if(isAllFieldsEntered && isFieldValueChanged && isCalvingAgeInInterval && youngCattleWeightInRange) {
      fetchNewDlgFeedCalculation()
    }

  }, [watchFirstCalvingAge, watchHeiferRaisingMethod, watchYoungCattleWeightBeforeCalving])

  async function fetchNewDlgFeedCalculation() {
    try {
      const response = await ClimaCheckService.getClimaChDlgFeed(
        id,
        {
          heiferRaisingMethodId: watchHeiferRaisingMethod,
          firstCalvingAge: watchFirstCalvingAge?.toString()?.replace(',', '.'),
          youngCattleWeightBeforeCalving: watchYoungCattleWeightBeforeCalving
        }
      )
      setValue('rearingPhases', response.data)
    } catch(error) {
      handleErrors(error)
    }
  }

  function getFeedRationsTabActive() {
    const cowsFeedRationsTab = steps.findIndex(step => step.id === StepId.DAIRY_COW_FEED)
    const heifersFeedRationsTab = steps.findIndex(step => step.id === StepId.HEIFER_FEED)
    const isFeedCowFeedTabActive = activeTab === cowsFeedRationsTab
    const isHeiferFeedTabActive = activeTab === heifersFeedRationsTab
    
    return watchRaisesHeifers ?
      isFeedCowFeedTabActive || isHeiferFeedTabActive :
      isFeedCowFeedTabActive
  }

  async function handleCo2Initial() {
    try {
      const response = await ClimaCheckService.getClimaChAnswOptions(id, { farmerId })
      if (response.data.farmer.totalMilkAmount) {
        const firstStepFieldsFiltered = response.data.useMilkPrice ? firstStepFields : firstStepFields.filter(field => field !== 'milkPrice')
        firstStepFieldsFiltered.map(field => {
          methods.setValue(field, response.data.farmer[field])
        })
      }
      const stepsFiltered = response.data.applyForageProductionInDataCollection ?
        allSteps :
        allSteps.filter(step => !step.forageStep)
      setOptions(response.data)
      setSteps(stepsFiltered)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleDraft() {
    try {
      const response = await ClimaCheckService.getClimaChAnswOptions(id, { farmerId })
      const responseData = await ClimaCheckService.getClimaChAnswers(id, { farmerId })
      methods.reset({
        ...responseData.data,
        sellOtherMilkSelected: Boolean(responseData.data?.otherMilkAmount)
      })
      if (response.data.farmer.totalMilkAmount) {
        const firstStepFieldsFiltered = response.data.useMilkPrice ? firstStepFields : firstStepFields.filter(field => field !== 'milkPrice')
        firstStepFieldsFiltered.map(field => {
          methods.setValue(field, response.data.farmer[field])
        })
      }
      const stepsFiltered = response.data.applyForageProductionInDataCollection ?
        allSteps :
        allSteps.filter(step => !step.forageStep)

      setOptions(response.data)
      setSteps(!responseData.data.raiseHeifersOnFarm ? stepsFiltered.filter(step => !step?.calfStep) : stepsFiltered)
      setActiveTab(responseData.data?.step || 0)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handleEdit() {
    try {
      const response = await ClimaCheckService.getClimaChAnswOptions(id, { farmerId })
      const responseData = await ClimaCheckService.getClimaChAnswers(id, { farmerId })
      methods.reset({
        ...responseData.data,
        sellOtherMilkSelected: Boolean(responseData.data?.otherMilkAmount)
      })
      const stepsFiltered = response.data.applyForageProductionInDataCollection ?
        allSteps :
        allSteps.filter(step => !step.forageStep)
      setOptions(response.data)
      setSteps(stepsFiltered)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  function handleExpired() {
    notify(t('Co2 climate slot expired'), 'error')
    navigate(RouteTypes.CO2_CLIMATE)
  }

  async function fetchOptions() {
    setIsBusy(true)
    try {
      /* answerStatus = 0 - initial answer (no draft, no answer yet, co2 slot active)
      answerStatus = 1 -  edit
      answerStatus = 2 - draft
      answerStatus = 3 - not available anymore (expired) */
      const responseStatus = await ClimaCheckService.checkClimaChStatus(id, { farmerId })
      switch (responseStatus.data.answerStatus) {
        //initial answer (no draft, no answer yet, co2 slot active)
        case 0:
          return handleCo2Initial()
        //edit
        case 1:
          return handleEdit()
        //draft
        case 2:
          return handleDraft()
        //not available anymore (expired)
        case 3:
          return handleExpired()
      }
    } catch (error) {
      handleErrors(error)
      navigate(RouteTypes.CO2_CLIMATE)
    }
    setIsBusy(false)
  }

  function renderSteps() {
    return <StepsContext.Provider
      value={{
        steps,
        hansleStepClick: (index) => {
          setActiveTab(index);
          methods.clearErrors();
        },
        activeTab,
        isLoadingResults,
        breed: watchBreedField?.length > 0,
        lastStepIndex: steps.length - 1
      }}
    >
      <StepsContainer />
    </StepsContext.Provider>
  }

  function renderActiveComponentStep() {
    const id = steps[activeTab].id
    switch (id) {
      case StepId.DAIRY_DATA:
        return <FirstStep
          options={options}
          setOptions={setOptions}
        />
      case StepId.DAIRY_COW_METRICS:
        return <SecondStep
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
          breeds={options?.breeds || []}
        />
      case StepId.HEIFER_METRICS:
        return <ThirdStep
          youngCattleBreeds={options?.youngCattleBreeds || []}
          heiferRaisingMethods={options?.heiferRaisingMethods || []}
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
        />
      case StepId.CALF_REARING:
        return <FourthStep
          calfRearingTypes={options?.calfRearingTypes || []}
          setIsLoadingResults={(value) => setIsLoadingResults(value)}
        />
      case StepId.DAIRY_COW_HUSBANDRY:
        return <FifthStep
          housingTypes={options?.housingTypes || []}
        />
      case StepId.HEIFER_HUSBANDRY:
        return <SixthStep
          housingTypes={options?.housingTypes || []}
        />
      case StepId.FORAGE_PRODUCTION:
        return <ForageProduction
          options={options}
        />
      case StepId.DAIRY_COW_MANURE:
        return <SeventhStep
          options={options?.manureStorageTypes || []}
          manureFermentations={options?.manureFermentations || []}
        />
      case StepId.HEIFER_MANURE:
        return <EightStep
          options={options?.manureStorageTypes || []}
          manureFermentations={options?.manureFermentations || []}
        />
      case StepId.DAIRY_COW_ENERGY:
        return <NineStep
          energyConsumptions={options?.energyConsumptions}
        />
      case StepId.HEIFER_ENERGY:
        return <TenStep
          energyConsumptions={options?.energyConsumptions}
        />
      case StepId.PERFORMANCE_GROUP:
        return <ElevenStep
          milkOptions={options}
        />
      case StepId.DAIRY_COW_FEED:
        return <TwelveStep
          milkOptions={options}
        />
      case StepId.HEIFER_FEED:
        return <ThirteenStep
          milkOptions={options}
        />
      case StepId.FORAGE_FEEDS:
        return <ForageFeed
          silageCoarseFeeds={options?.silageCoarseFeeds || []}
          silageConcentratedFeeds={options?.silageConcentratedFeeds || []}
        />
      case StepId.ADDITIVES_USAGE:
        return <FourteenStep
          emissionReducingAdditives={options?.emissionReducingAdditives || []}
        />
      case StepId.LAND_USE:
        return <FifthteenStep
          landTypes={options?.landTypes || []}
        />
      case StepId.GHG_RESULT:
        return <Results
          results={results}
          removeMargin
        />
    }
  }

  async function handleSubmitCo2Answers(data) {
    const payload = prepareDataForPayload(data)
    setIsBusy(true)
    try {
      await ClimaCheckService.storeClimaChAnsw(id, { ...payload, farmerId })
      setIsSubmitModalOpen(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleSubmitNextStep() {
    const FEED_GROUPS_TAB = steps?.findIndex(step => step.feedGroupTab)
    if (activeTab === FEED_GROUPS_TAB) {
      const feedsGroup = methods.getValues('feedGroups')
      const phaseEnabled = methods.getValues('phaseFeeding')
      const feedGroups = phaseEnabled ? getTransformFeedGroups(feedsGroup) : feedsGroup
      const rearingPhases = watchRaisesHeifers ? methods.getValues('rearingPhases') : []
      let hasErrors = false
      let countCoarseFeeds = 0
      feedGroups.map(feedGroup => {
        const numberOfFeedsAssign = getNumberOfFeeds(feedGroup);
        countCoarseFeeds += feedGroup?.coarseFeeds?.length
        if (numberOfFeedsAssign === 0) {
          const feedElement = document.getElementById(feedGroup?.name);
          if (feedElement) {
            feedElement.classList.add('error-feed-group');
            hasErrors = true
          }
        }
      });
      rearingPhases.map(phase => {
        const numberOfFeedsAssign = getNumberOfFeeds(phase);
        countCoarseFeeds += phase?.coarseFeeds?.length
        if (numberOfFeedsAssign === 0) {
          const feedElement = document.getElementById(phase?.name);
          if (feedElement) {
            feedElement.classList.add('error-feed-group');
            hasErrors = true
          }
        }
      });
      if (!hasErrors && countCoarseFeeds > 0) {
        handleSaveDraft()
      }
      if (countCoarseFeeds === 0) {
        notify(t('Please select at least one coarse feed'), 'error')
      }
      return
    }
    handleSaveDraft()
  }

  async function onSubmit(data) {
    const LAST_STEP_INDEX = steps.length - 1
    if (activeTab === LAST_STEP_INDEX) {
      handleSubmitCo2Answers(cloneDeep(data))
    } else if (steps[activeTab + 1].id === StepId.GHG_RESULT) {
      setActiveTab(activeTab + 1)
      handleLoadResults(cloneDeep(data))
    } else {
      handleSubmitNextStep()
    }
  }

  function onError() {
  // TODO: Handle errors on different tabs
  //  notify('You have error on another tab', 'error');
  }

  async function handleLoadResults(data) {
    setIsLoadingResults(true)
    try {
      let responseHeifers
      const payload = prepareDataForPayload(data)
      await ClimaCheckService.saveClimaChDraft(id, { ...payload, farmerId })
      const responseCows = await ClimaCheckService.getCowsResults(id, { ...payload, farmerId })
      if(watchRaisesHeifers) {
        responseHeifers = await ClimaCheckService.getHeifersResults(id, { ...payload, farmerId })
      }
      setResults({
        cowResults: responseCows.data,
        heiferResults: responseHeifers?.data
      })
    } catch (error) {
      setActiveTab(activeTab - 1)
      handleErrors(error)
    }
    setIsLoadingResults(false)
  }

  function handleSubmitPhases() {
    const tempFeedGroups = cloneDeep(watchFeedGroups)
    const selectedFeedGroups = methods.getValues('selectedFeedGroups')
    const feedingPhases = methods.getValues('feedingPhases')

    tempFeedGroups.forEach((feedGroup, index) => {
      const selectedGroup = selectedFeedGroups.find(selectedFeedGroupIndex => selectedFeedGroupIndex === index)
      if (selectedGroup !== undefined) {
        if (feedGroup.feedingPhases?.length > 0) {
          feedGroup.feedingPhases.forEach((feedingPhase, index) => {
            const phaseData = index === 0 ? feedingPhases[0] : feedingPhases[1]
            feedingPhase = {
              ...feedingPhase,
              ...phaseData
            }
          })
        } else {
          feedGroup.feedingPhases = [
            {
              ...feedingPhases[0],
              concentratedFeeds: [],
              juicyFeeds: [],
              coarseFeeds: []
            },
            {
              ...feedingPhases[1],
              concentratedFeeds: [],
              juicyFeeds: [],
              coarseFeeds: []
            }
          ]
        }
      } else {
        tempFeedGroups[index] = {
          ...feedGroup,
          feedingPhases: []
        }
      }
    })
    methods.setValue('feedGroups', tempFeedGroups)
    setFeedPage(1)
  }

  function renderPreviousButton() {
    return <Button
      type='button'
      disabled={isLoadingResults}
      onClick={() => {
        methods.clearErrors()
        setActiveTab(activeTab - 1)
      }}
      content={t('Previous')}
    />
  }

  /*  function renderFeedRationsButton() {
    if (energyCalcData.energyAddition < 0 || energyCalcData.energyAddition >= 100) {
      return renderCalcEnergyButton()
    }
    return <Button
      type={'submit'}
      content={t('Calculate GHG emissions')}
      style={{ minWidth: 250 }}
    />
  } */

  function renderNextButton() {
    const isForageStep = steps[activeTab].id === StepId.FORAGE_PRODUCTION && watchGrowSilage !== NO_SILAGE_KEY
    return <Button
      type={'submit'}
      loading={isLoadingResults}
      style={{ minWidth: 'fit-content' }}
      disabled={!enableNextButton || isForageStep}
      content={'Speichern & Weiter'}
    />
  }

  function renderSubmitButton() {
    return <Button
      style={{ minWidth: 225 }}
      disabled={Object.keys(methods.formState.errors).length > 0 || isLoadingResults}
      content={t('Complete data collection')}
    />
  }

  function renderAssignFeedButton() {
    return <Button
      type={'button'}
      style={{ minWidth: 200 }}
      content={t('Assign feeds')}
      onClick={() => {
        const selectedFeedGroups = methods.getValues('selectedFeedGroups')
        const feedingPhases = methods.getValues('feedingPhases')

        if(feedingPhases?.length > 0 && (!selectedFeedGroups || selectedFeedGroups?.length === 0)) {
          notify('Bitte wählen Sie eine Leistungsgruppe aus.', 'error')
          return
        }
        if(watchRaisesHeifers && !watchRearingPhaseType) {
          methods.setError('rearingPhasesType', { message: '' })
          return
        }
        if(watchPhaseFeeding) {
          handleSubmitPhases()
        } else {
          setFeedPage(1)
        }
      }}
    />
  }

  function renderBackToFeedGroups() {
    return <Button
      type={'button'}
      style={{ minWidth: 210 }}
      content={t('Back to performance groups')}
      onClick={() => setFeedPage(0)}
    />
  }

  async function getEnergyData() {
    const data = methods.getValues()
    const payload = prepareDataForPayload(data)
    const cowsFeedRationsTab = steps[activeTab].id === StepId.DAIRY_COW_FEED
    const feedRationsType = cowsFeedRationsTab ? 'cows' : 'heifers' as ClimaCheckFeedType
    setLoadingEnergyData(true)
    try {
      const response = await ClimaCheckService.getClimaChEnergyAddition(id, feedRationsType, { ...payload, farmerId })
      setIsEnergyModalOpen(true)
      setEnergyCalcData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingEnergyData(false)
  }

  function renderCalcEnergyButton() {
    const isHefeirsStep = steps[activeTab].id === StepId.HEIFER_FEED
    const energyButtonTitle = isHefeirsStep ? 'Futterwerte übernehmen' : t('Calculate energy requirements')
    return <Button
      type='button'
      content={energyButtonTitle}
      onClick={() => getEnergyData()}
      loading={loadingEnergyData}
      disabled={loadingEnergyData}
      style={{ minWidth: 250 }}
    />
  }

  function renderAnwseredPercent() {
    return <AnsweredContainer>
      <Progress value={activeTab + 1} total={steps.length} progress={'ratio'} />
    </AnsweredContainer>
  }

  async function handleSaveDraft() {

    if(watchBreedField?.length > 0 && (!watchFeedGroups || watchFeedGroups?.length === 0)) {
      return notify('Es fehlt Laktierende oder Trockensteher. Bitte Rasse erneut auswählen.', 'error')
    }

    const data = methods.getValues()
    const payload = prepareDataForPayload(data)
    setIsLoadingResults(true)
    try {
      await ClimaCheckService.saveClimaChDraft(id, { ...payload, step: activeTab + 1 > lastStep ? activeTab + 1 : lastStep, farmerId })
      setActiveTab(activeTab + 1)
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingResults(false)
  }

  function renderEnergyModal() {
    const isHefeirsStep = steps[activeTab].id === StepId.HEIFER_FEED
    return <EnergyModal
      energyCalcData={energyCalcData}
      isEnergyModalOpen={isEnergyModalOpen}
      handleClose={() => setIsEnergyModalOpen(false)}
      heiferEnergyModal={isHefeirsStep}
    />
  }

  function renderSubmitModal() {
    return <Dialog
      textHeader={''}
      isDialogOpen={isSubmitModalOpen}
      textSubmit={t('Back to home page')}
      hideCloseIcon
      handleSubmit={() => {
        setIsSubmitModalOpen(false)
        navigate(RouteTypes.CLIMATE_CHECK)
      }
      }
      textContent={<div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 20 }}>
          <SubmitHeader>{'Vielen Dank für Ihre Dateneingabe.'}</SubmitHeader>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div>{'Sie haben die Möglichkeit die Daten im Rahmen des Erhebungszeitraums zu ändern.'}</div>
          <div>{'Nach dem Ende des Erhebungszeitraums erhalten Sie ein ausführliches PDF-Dokument mit allen Auswertungen und Eingabewerten.'}</div>
        </div>
        <LogoWrapper style={{ backgroundImage: `url(${logoTitle})` }} />
      </div>}
    />
  }

  const LAST_STEP = steps.length - 1

  // we cannon procced if we have not selected breed unless we are on first step and on the last step we have to submit
  const enableNextButton = activeTab === 0 || (activeTab < LAST_STEP && Boolean(watchBreedField) && watchBreedField?.length > 0)

  const FEED_GROUPS_TAB = steps?.findIndex(step => step.feedGroupTab)

  const assignFeedButton = (activeTab === FEED_GROUPS_TAB) && (feedPage === 0)

  const backToFeedGroups = (activeTab === FEED_GROUPS_TAB) && (feedPage === 1)

  const isFeedRationsTabsActive = getFeedRationsTabActive()
  
  const displayEnergyCalcData = isFeedRationsTabsActive && (!energyCalcData || (energyCalcData?.energyAddition < 0 || energyCalcData?.energyAddition >= 100))

  return (
    <Layout
      title={t('Climate check')}
      segmented
      close={RouteTypes.CLIMATE_CHECK}
      isBusy={isBusy}
      isCo2Loader={true}
      actions={renderAnwseredPercent()}
    >
      {renderEnergyModal()}
      {renderSubmitModal()}
      <CheckClimaEl.StepsContainer>
        {renderSteps()}
        <Message info style={{
          borderRadius: '10px',
          marginLeft: 'auto',
          marginRight: 'auto',
          width: 'fit-content',
          textAlign: 'center'
        }}>
          <Message.Content>
            {!isBusy && <div dangerouslySetInnerHTML={{ __html: getStepHeadMessage(steps[activeTab].id as StepId) }}/>}
          </Message.Content>
        </Message>
      </CheckClimaEl.StepsContainer>
      <CheckClimaEl.WizzardContentWrapper onSubmit={methods.handleSubmit(onSubmit, onError)}>
        <CheckClimaEl.StyledContent $isFirstStep={activeTab === 0}>
          <FeedPageContext.Provider value={{
            feedPage,
            setFeedPage,
            energyCalcData,
            setEnergyCalcData,
            loadingEnergyData,
            handleSubmit: methods.handleSubmit(onSubmit)
          }}>
            <FormProvider {...methods}>
              {!isBusy && options && renderActiveComponentStep()}
            </FormProvider>
          </FeedPageContext.Provider>
        </CheckClimaEl.StyledContent>
        <CheckClimaEl.ActionsContainer $activeTab={activeTab}>
          {activeTab > FIRST_STEP_INDEX && !backToFeedGroups && renderPreviousButton()}
          {backToFeedGroups && renderBackToFeedGroups()}
          {enableNextButton && !assignFeedButton && !displayEnergyCalcData && renderNextButton()}
          {assignFeedButton && renderAssignFeedButton()}
          {activeTab === LAST_STEP && renderSubmitButton()}
          {displayEnergyCalcData && renderCalcEnergyButton()}
        </CheckClimaEl.ActionsContainer>
      </CheckClimaEl.WizzardContentWrapper>
    </Layout >
  );
};

export default AnswerClimateCheck;